/**
 * Vue app runtime / migration telemetry for @vue/compat MODE 2.
 *
 * - errorHandler: real render/lifecycle crashes → logger.error (/log/error + Sentry)
 * - warnHandler: allowlist only (resolve / emits gaps that may mean a missed
 *   MODE 2 breaking fix). Soft MODE 2 deprecations and unrelated quality warns
 *   are not sent to /log/debug.
 *
 * Vuex unknown getter/action/mutation + illegal mutation are reported separately
 * via vuex-soft-strict.js → /log/debug.
 */
import logger from '@/utils/global-logger';
import { VueErrorCode } from '@/utils/error-codes/vue';
import { DebugCode } from '@/utils/error-codes/debug';

const reportedRuntimeKeys = new Set();
const reportedWarnKeys = new Set();

/**
 * Soft @vue/compat MODE 2 deprecation feature flags.
 * Under MODE 2 these keep Vue 2 behavior — logging them does not mean a MODE 2 gap.
 */
const SOFT_MODE2_DEPRECATION_RE =
  /\(deprecation\s+(?:ATTR_FALSE_VALUE|ATTR_ENUMERATED_COERCION|WATCH_ARRAY|OPTIONS_DATA_MERGE|OPTIONS_BEFORE_DESTROY|OPTIONS_DESTROYED|OPTIONS_DATA_FN|CUSTOM_DIR|FILTERS|INSTANCE_[A-Z0-9_]+|GLOBAL_[A-Z0-9_]+|TRANSITION_[A-Z0-9_]+|COMPILER_[A-Z0-9_]+|RENDER_FUNCTION|COMPONENT_V_MODEL|COMPONENT_ASYNC|PROPS_DEFAULT_THIS|ATTR_ENUMERATED)\)/i;

/**
 * Allowlist: Vue warns that may indicate a missed MODE 2 / Vue 3 migration gap.
 * Everything else (name clashes, generic quality warns) is noise for /log/debug.
 */
const MODE2_MISS_WARN_RE =
  /Failed to resolve (?:component|directive)|Extraneous non-emits event listeners/i;

function getBuildTag() {
  return (typeof process !== 'undefined' && process.env && process.env.CB_BUILD_TAG) || '0.0.0';
}

function getLocationMeta() {
  if (typeof window === 'undefined' || !window.location) {
    return { path: '', href: '' };
  }
  const path = window.location.pathname || '';
  // Strip query/hash — HP claim/session tokens live in the query string.
  const origin = window.location.origin || '';
  const href = origin
    ? `${origin}${path}`
    : (window.location.href || '').split(/[?#]/)[0];
  return { path, href };
}

function isCbDebugEnabled() {
  return typeof window !== 'undefined' && !!window.__cb_debug;
}

function resolveComponentName(instance) {
  if (!instance) return undefined;
  return (
    instance.$options?.name
    || instance.type?.name
    || instance.$.type?.name
    || instance.$.type?.__name
    || undefined
  );
}

/** @private — exported for unit tests */
export function resetVueRuntimeErrorHandlerState() {
  reportedRuntimeKeys.clear();
  reportedWarnKeys.clear();
}

/**
 * Whether a Vue warning should be sent to /log/debug.
 * Allowlist MODE-2-miss signals only (resolve / emits). Soft deprecations and
 * unrelated quality warns (e.g. computed/method name clashes) are skipped.
 * @param {unknown} msg
 * @returns {boolean}
 */
export function shouldLogVueWarning(msg) {
  const text = String(msg || '').trim();
  if (!text) {
    return false;
  }
  if (SOFT_MODE2_DEPRECATION_RE.test(text)) {
    return false;
  }
  // Generic compat deprecation phrasing without a hard MODE-2 miss
  if (/\(deprecation\s+[A-Z0-9_]+\)/i.test(text)) {
    return false;
  }
  return MODE2_MISS_WARN_RE.test(text);
}

/** @deprecated Use shouldLogVueWarning — kept for any external imports. */
export function shouldLogCompatWarning(msg) {
  return shouldLogVueWarning(msg);
}

/**
 * Install Vue errorHandler + warnHandler (MODE-2 miss signals → /log/debug).
 * Safe to call once per app from configureApp.
 * @param {import('vue').App} app
 */
export function installVueRuntimeErrorHandlers(app) {
  if (!app || !app.config) {
    return;
  }

  app.config.errorHandler = (err, instance, info) => {
    if (isCbDebugEnabled() && typeof console !== 'undefined' && console.error) {
      console.error('CB_BOOT: app error', err, info, instance);
    }

    const message = err instanceof Error ? err.message : String(err);
    const dedupeKey = `${info || ''}|${message}`;
    if (reportedRuntimeKeys.has(dedupeKey)) {
      return;
    }
    reportedRuntimeKeys.add(dedupeKey);

    const { path, href } = getLocationMeta();
    const cause = err instanceof Error ? err : new Error(message);
    // logger.error rethrows in non-production (logger._e). An errorHandler must never
    // throw (it would escalate a handled error into an uncaught crash), so swallow it here.
    try {
      logger.error(
        VueErrorCode.VUE_RUNTIME,
        {
          path,
          href,
          build: getBuildTag(),
          info: info || undefined,
          component: resolveComponentName(instance),
        },
        cause
      );
    } catch (_e) {
      /* dev-only rethrow from logger._e; already reported to /log/error + Sentry */
    }
  };

  app.config.warnHandler = (msg, instance, trace) => {
    if (isCbDebugEnabled() && typeof console !== 'undefined' && console.warn) {
      console.warn('CB_BOOT: app warning', msg, trace, instance);
    }

    if (!shouldLogVueWarning(msg)) {
      return;
    }

    const text = String(msg || '').slice(0, 500);
    const dedupeKey = text.slice(0, 120);
    if (reportedWarnKeys.has(dedupeKey)) {
      return;
    }
    reportedWarnKeys.add(dedupeKey);

    const { path, href } = getLocationMeta();
    // /log/debug: MODE 2 miss telemetry — not Sentry / error counts.
    logger.debug(DebugCode.VUE_COMPAT_WARNING, {
      path,
      href,
      build: getBuildTag(),
      msg: text,
      trace: String(trace || '').slice(0, 500) || undefined,
      component: resolveComponentName(instance),
    });
  };
}
